/**
 * Chart options for the MD analysis sections: pure functions from the analysis
 * data to an ECharts option, so each chart is unit-testable without a browser.
 * The React wrapper is ui/charting/EChart.jsx. Reports reuse these options.
 *
 * The first section is the protein-ligand RMSD: the protein backbone RMSD on
 * the left axis and the ligand RMSD on the right, against time, for one run or
 * all runs of a compound.
 */

/** Shades per run: protein lines blue-grey, ligand lines wine, lighter or darker per run. */
export const RMSD_PROTEIN_SHADES = ['#4f7a9a', '#2d516b', '#86a8c0', '#1f3b52', '#aec5d6'];
export const RMSD_LIGAND_SHADES = ['#a8325e', '#7a1f42', '#d06a8e', '#5c1432', '#e3a1b9'];

const NO_DATA = (v) => v === null || v === undefined || Number.isNaN(Number(v));

/**
 * The runs of a compound that have an RMSD series: time in ns and the two
 * RMSD columns, aligned by frame.
 * @param {object} compound - a `compounds` entry of the md/1 packet
 * @returns {{ run: number, time_ns: number[], protein: Array<number|null>,
 *   ligand: Array<number|null> }[]}
 */
export function rmsdRuns(compound) {
    const runs = [];
    for (const c of compound?.copies || []) {
        const t = c.series?.time_ps || [];
        const protein = c.series?.backbone_rmsd_a || [];
        const ligand = c.series?.ligand_rmsd_a || [];
        const hasData = (col) => col.some((v) => !NO_DATA(v));
        if (t.length === 0 || (!hasData(protein) && !hasData(ligand))) continue;
        runs.push({
            run: c.copy,
            time_ns: t.map((x) => Number(x) / 1000),
            protein: t.map((_, i) => (NO_DATA(protein[i]) ? null : Number(protein[i]))),
            ligand: t.map((_, i) => (NO_DATA(ligand[i]) ? null : Number(ligand[i]))),
        });
    }
    return runs;
}

/**
 * The ECharts option of the protein-ligand RMSD chart.
 * @param {object} compound - a `compounds` entry of the md/1 packet
 * @param {object} [opts]
 * @param {'all'|number} [opts.run] - one run, or 'all' (default)
 * @param {string} [opts.textColor] - axis and legend text color
 * @param {boolean} [opts.interactive] - false for a report picture: no zoom control or tooltip
 * @returns {object|null} null when there is no RMSD data
 */
export function rmsdChartOption(compound, {
    run = 'all', textColor = '#444', interactive = true,
} = {}) {
    const all = rmsdRuns(compound);
    const runs = run === 'all' ? all : all.filter((r) => r.run === Number(run));
    if (runs.length === 0) return null;
    const multiple = runs.length > 1;
    // A report picture is scaled down to fit a page, so its text and lines are drawn larger.
    const fontSize = interactive ? 12 : 20;
    const lineWidth = interactive ? 1.2 : 2.2;
    const series = [];
    runs.forEach((r, i) => {
        const protein = RMSD_PROTEIN_SHADES[i % RMSD_PROTEIN_SHADES.length];
        const ligand = RMSD_LIGAND_SHADES[i % RMSD_LIGAND_SHADES.length];
        const suffix = multiple ? `, run ${r.run}` : '';
        const base = {
            type: 'line',
            showSymbol: false,
            sampling: 'lttb',
            connectNulls: false,
            animation: false,
        };
        series.push({
            ...base,
            name: `Protein${suffix}`,
            yAxisIndex: 0,
            lineStyle: { width: lineWidth, color: protein },
            itemStyle: { color: protein },
            data: r.time_ns.map((x, k) => [x, r.protein[k]]),
        });
        series.push({
            ...base,
            name: `Ligand${suffix}`,
            yAxisIndex: 1,
            lineStyle: { width: lineWidth, color: ligand },
            itemStyle: { color: ligand },
            data: r.time_ns.map((x, k) => [x, r.ligand[k]]),
        });
    });
    const axisText = { color: textColor, fontSize };
    const fmt = (v) => (v === null || v === undefined ? '' : Number(v).toFixed(2));
    return {
        animation: false,
        textStyle: axisText,
        grid: {
            left: interactive ? 56 : 90,
            right: interactive ? 56 : 90,
            top: interactive ? 44 : 70,
            bottom: interactive ? 64 : 70,
        },
        legend: { top: 4, textStyle: axisText },
        tooltip: interactive ? { trigger: 'axis', valueFormatter: fmt } : { show: false },
        xAxis: {
            type: 'value',
            name: 'Time (ns)',
            nameLocation: 'middle',
            nameGap: interactive ? 28 : 44,
            min: 0,
            axisLabel: axisText,
            nameTextStyle: axisText,
        },
        yAxis: [
            {
                type: 'value',
                name: 'Protein RMSD (Å)',
                position: 'left',
                min: 0,
                axisLine: { show: true, lineStyle: { color: RMSD_PROTEIN_SHADES[0] } },
                axisLabel: { color: RMSD_PROTEIN_SHADES[0], fontSize },
                nameTextStyle: { color: RMSD_PROTEIN_SHADES[0], align: 'left', fontSize },
                splitLine: { lineStyle: { opacity: 0.15 } },
            },
            {
                type: 'value',
                name: 'Ligand RMSD (Å)',
                position: 'right',
                min: 0,
                axisLine: { show: true, lineStyle: { color: RMSD_LIGAND_SHADES[0] } },
                axisLabel: { color: RMSD_LIGAND_SHADES[0], fontSize },
                nameTextStyle: { color: RMSD_LIGAND_SHADES[0], align: 'right', fontSize },
                splitLine: { show: false },
            },
        ],
        dataZoom: interactive ? [
            // No `inside` zoom: ECharts' inside zoom traps the mouse wheel even when its wheel
            // zoom is off, and these charts nearly fill the results pane, so the pane could not be
            // scrolled over them. The slider below the chart zooms instead.
            {
                type: 'slider', xAxisIndex: 0, height: 14, bottom: 8,
            },
        ] : [],
        series,
    };
}

/** What the chart's two quantities are, for its caption (and later the report). */
export const RMSD_CHART_CAPTION = 'Root mean square deviation against time, from the submitted pose. '
    + 'Protein (left axis): backbone atoms (N, CA, C) after superimposing each frame on the '
    + 'submitted structure. Ligand (right axis): heavy atoms after that backbone fit, without '
    + 'refitting, so it reports how far the ligand has moved with respect to the protein. The '
    + 'value at the start of the run is not zero: the system was minimized and equilibrated '
    + 'before production. A ligand RMSD much larger than the protein\'s suggests the ligand has '
    + 'left its starting site.';

// ---------------------------------------------------------------------------
// Ligand RMSF
// ---------------------------------------------------------------------------

export const LIGAND_RMSF_CAPTION = 'Root mean square fluctuation of each ligand heavy atom about its '
    + 'mean position over the run, after fitting the protein backbone, so it reports how much each '
    + 'part of the ligand moves with respect to the protein. Atoms are numbered as in the '
    + 'submitted molecule. Large values mark flexible groups. With several independent runs the '
    + 'line is their mean, with the shaded band one standard deviation across them.';

/**
 * The per-atom RMSF rows to draw: one run's, or the mean across runs.
 * @param {object} compound - a `compounds` entry of the md/1 packet
 * @param {'mean'|number} run
 * @returns {{ atom: number, element: string, rmsf: number, sd: number|null }[]}
 */
export function ligandRmsfRows(compound, run = 'mean') {
    if (run !== 'mean') {
        const copy = (compound?.copies || []).find((c) => c.copy === Number(run));
        return (copy?.ligand_rmsf || []).map((r) => ({
            atom: r.atom, element: r.element, rmsf: r.rmsf_a, sd: null,
        }));
    }
    const agg = compound?.aggregate?.ligand_rmsf;
    if (agg && agg.length) {
        const single = (compound.copies || []).length < 2;
        return agg.map((r) => ({
            atom: r.atom, element: r.element, rmsf: r.rmsf_a, sd: single ? null : r.rmsf_sd,
        }));
    }
    const only = (compound?.copies || []).find((c) => (c.ligand_rmsf || []).length);
    return (only?.ligand_rmsf || []).map((r) => ({
        atom: r.atom, element: r.element, rmsf: r.rmsf_a, sd: null,
    }));
}

/** The runs of a compound that have ligand RMSF data. */
export function ligandRmsfRuns(compound) {
    return (compound?.copies || []).filter((c) => (c.ligand_rmsf || []).length > 0)
        .map((c) => c.copy);
}

/**
 * The ECharts option of the ligand RMSF chart (atom index on the x axis).
 * @param {object} compound
 * @param {object} [opts]
 * @param {'mean'|number} [opts.run]
 * @param {string} [opts.textColor]
 * @param {boolean} [opts.interactive]
 * @returns {object|null} null when there is no RMSF data
 */
export function ligandRmsfChartOption(compound, {
    run = 'mean', textColor = '#444', interactive = true,
} = {}) {
    const rows = ligandRmsfRows(compound, run);
    if (rows.length === 0) return null;
    const fontSize = interactive ? 12 : 20;
    const color = RMSD_LIGAND_SHADES[0];
    const axisText = { color: textColor, fontSize };
    const atoms = rows.map((r) => String(r.atom));
    const banded = rows.some((r) => r.sd !== null);
    const series = [];
    if (banded) {
        series.push({
            name: 'lower',
            type: 'line',
            stack: 'sd',
            symbol: 'none',
            lineStyle: { opacity: 0 },
            data: rows.map((r) => Math.max(0, r.rmsf - (r.sd || 0))),
            tooltip: { show: false },
            silent: true,
        });
        series.push({
            name: 'band',
            type: 'line',
            stack: 'sd',
            symbol: 'none',
            lineStyle: { opacity: 0 },
            areaStyle: { color, opacity: 0.18 },
            data: rows.map((r) => 2 * (r.sd || 0)),
            tooltip: { show: false },
            silent: true,
        });
    }
    series.push({
        name: run === 'mean' ? 'Mean of runs' : `Run ${run}`,
        type: 'line',
        symbolSize: interactive ? 6 : 10,
        lineStyle: { width: interactive ? 1.6 : 3, color },
        itemStyle: { color },
        data: rows.map((r) => r.rmsf),
        animation: false,
    });
    return {
        animation: false,
        textStyle: axisText,
        grid: {
            left: interactive ? 56 : 90,
            right: interactive ? 24 : 40,
            top: interactive ? 20 : 30,
            bottom: interactive ? 44 : 70,
        },
        tooltip: interactive ? {
            trigger: 'axis',
            formatter: (params) => {
                const p = params.find((x) => x.seriesName !== 'lower' && x.seriesName !== 'band');
                if (!p) return '';
                const row = rows[p.dataIndex];
                const sd = row.sd === null ? '' : ` ± ${row.sd.toFixed(2)}`;
                return `Atom ${row.atom} (${row.element}): ${row.rmsf.toFixed(2)}${sd} Å`;
            },
        } : { show: false },
        xAxis: {
            type: 'category',
            data: atoms,
            name: 'Ligand atom index',
            nameLocation: 'middle',
            nameGap: interactive ? 28 : 46,
            axisLabel: { ...axisText, interval: atoms.length > 40 ? 'auto' : 0 },
            nameTextStyle: axisText,
        },
        yAxis: {
            type: 'value',
            name: 'RMSF (Å)',
            min: 0,
            axisLabel: axisText,
            nameTextStyle: { ...axisText, align: 'left' },
            splitLine: { lineStyle: { opacity: 0.15 } },
        },
        series,
    };
}

// ---------------------------------------------------------------------------
// Ligand properties
// ---------------------------------------------------------------------------

/**
 * The ligand property panels, in report order: the series of a run in the
 * packet, the axis label and the line color.
 */
export const LIGAND_PROPERTY_PANELS = [
    { key: 'ligand_rmsd_a', label: 'RMSD (Å)', color: '#2f7fb8' },
    { key: 'ligand_rgyr_a', label: 'rGyr (Å)', color: '#6aa84f' },
    { key: 'ligand_intrahb', label: 'intraHB', color: '#3b78b5' },
    { key: 'ligand_molsa_a2', label: 'MolSA (Å²)', color: '#d98c2b' },
    { key: 'ligand_sasa_a2', label: 'SASA (Å²)', color: '#5b9bd0' },
    { key: 'ligand_psa_a2', label: 'PSA (Å²)', color: '#a0522d' },
];

export const LIGAND_PROPERTIES_CAPTION = 'Ligand properties against time. RMSD: heavy-atom deviation '
    + 'from the submitted pose after fitting the protein backbone. rGyr: mass-weighted radius of '
    + 'gyration of the heavy atoms, a measure of how extended the ligand is. intraHB: number of '
    + 'intramolecular hydrogen bonds (H to acceptor within 2.5 Å, donor angle at least 120°, '
    + 'acceptor angle at least 90°). MolSA: the ligand\'s own van der Waals surface area. SASA: '
    + 'its solvent-accessible surface (1.4 Å probe) in the presence of the protein, so it falls as '
    + 'the ligand buries itself. PSA: solvent-accessible surface of the ligand alone contributed by '
    + 'nitrogen and oxygen. Each panel\'s histogram sums the shown runs.';

function blend(hex, amount) {
    const n = parseInt(hex.slice(1), 16);
    const mix = (c) => Math.round(c + (255 - c) * amount);
    const r = mix((n >> 16) & 255);
    const g = mix((n >> 8) & 255);
    const b = mix(n & 255);
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The runs of a compound with their property series (time in ns), for the panels that have data.
 * @param {object} compound
 * @returns {{ panels: object[], runs: { run: number, time_ns: number[],
 *   values: Object<string, Array<number|null>> }[] }}
 */
export function ligandPropertyRuns(compound) {
    const copies = (compound?.copies || []).filter((c) => (c.series?.time_ps || []).length > 0);
    const panels = LIGAND_PROPERTY_PANELS.filter((p) => copies.some(
        (c) => (c.series?.[p.key] || []).some((v) => !NO_DATA(v)),
    ));
    const runs = copies.map((c) => ({
        run: c.copy,
        time_ns: c.series.time_ps.map((t) => Number(t) / 1000),
        values: Object.fromEntries(panels.map((p) => [
            p.key, (c.series[p.key] || []).map((v) => (NO_DATA(v) ? null : Number(v))),
        ])),
    }));
    return { panels, runs };
}

/**
 * A round range covering [lo, hi]: its ends are multiples of a 1, 2 or 5 x 10^n step, so the
 * axis ticks are round numbers. A degenerate range is widened.
 * @param {number} lo
 * @param {number} hi
 * @returns {[number, number]}
 */
export function niceRange(lo, hi) {
    let a = lo;
    let b = hi;
    if (b - a < 1e-9) {
        a -= 0.5;
        b += 0.5;
    }
    const raw = (b - a) / 4;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 5, 10].map((m) => m * mag).find((x) => x >= raw);
    const round = (v) => Number(v.toPrecision(12));
    return [round(Math.floor(a / step) * step), round(Math.ceil(b / step) * step)];
}

/**
 * Counts of `values` in `bins` equal bins over a round range covering them (a degenerate range
 * is widened).
 * @param {number[]} values
 * @param {number} [bins]
 * @returns {{ lo: number, hi: number, counts: number[] }}
 */
export function histogramOf(values, bins = 12) {
    const finite = values.filter((v) => Number.isFinite(v));
    if (finite.length === 0) return { lo: 0, hi: 1, counts: Array(bins).fill(0) };
    const [lo, hi] = niceRange(Math.min(...finite), Math.max(...finite));
    const counts = Array(bins).fill(0);
    for (const v of finite) {
        counts[Math.min(bins - 1, Math.floor(((v - lo) / (hi - lo)) * bins))] += 1;
    }
    return { lo, hi, counts };
}

/** Picture size (layout pixels) of the properties chart for `panelCount` panels. */
export function ligandPropertiesSize(panelCount, interactive = false) {
    const row = interactive ? 100 : 150;
    return { width: 1200, height: panelCount * row + (interactive ? 40 : 70) };
}

/**
 * The ECharts option of the ligand properties chart: a time series and a
 * histogram for each property, stacked.
 * @param {object} compound
 * @param {object} [opts]
 * @param {'all'|number} [opts.run]
 * @param {string} [opts.textColor]
 * @param {boolean} [opts.interactive]
 * @returns {object|null} null when there is no property data
 */
export function ligandPropertiesChartOption(compound, {
    run = 'all', textColor = '#444', interactive = true,
} = {}) {
    const { panels, runs: all } = ligandPropertyRuns(compound);
    const runs = run === 'all' ? all : all.filter((r) => r.run === Number(run));
    const shown = panels.filter((p) => p.key !== 'ligand_rmsd_a'
        || runs.some((r) => r.values[p.key]?.some((v) => v !== null)));
    if (shown.length === 0 || runs.length === 0) return null;
    // The properties are only worth a chart when at least one beyond the RMSD exists.
    if (shown.length === 1 && shown[0].key === 'ligand_rmsd_a') return null;
    const fontSize = interactive ? 11 : 18;
    const axisText = { color: textColor, fontSize };
    const row = interactive ? 100 : 150;
    const top = interactive ? 18 : 30;
    const gap = interactive ? 26 : 46;
    const grids = [];
    const xAxis = [];
    const yAxis = [];
    const series = [];
    const maxTime = niceRange(0, Math.max(...runs.flatMap((r) => r.time_ns)))[1];
    const tick = (v) => String(Number(Number(v).toPrecision(4)));
    shown.forEach((panel, i) => {
        const y0 = top + i * row;
        const h = row - gap;
        const values = runs.flatMap((r) => r.values[panel.key]).filter((v) => v !== null);
        const hist = histogramOf(values);
        const last = i === shown.length - 1;
        // Line grid (left) and histogram grid (right), sharing the y range [lo, hi].
        grids.push({
            left: interactive ? 64 : 100, right: '24%', top: y0, height: h,
        });
        grids.push({
            left: '78%', right: interactive ? 16 : 30, top: y0, height: h,
        });
        const gi = 2 * i;
        xAxis.push({
            gridIndex: gi,
            type: 'value',
            min: 0,
            max: maxTime,
            axisLabel: { ...axisText, show: last, formatter: tick },
            name: last ? 'Time (ns)' : '',
            nameLocation: 'middle',
            nameGap: interactive ? 24 : 40,
            nameTextStyle: axisText,
            splitLine: { show: false },
        });
        xAxis.push({
            gridIndex: gi + 1, type: 'value', axisLabel: { show: false }, splitLine: { show: false },
        });
        yAxis.push({
            gridIndex: gi,
            type: 'value',
            min: hist.lo,
            max: hist.hi,
            name: panel.label,
            nameTextStyle: { ...axisText, align: 'left' },
            axisLabel: { ...axisText, formatter: tick },
            splitLine: { lineStyle: { opacity: 0.12 } },
        });
        const binWidth = (hist.hi - hist.lo) / hist.counts.length;
        yAxis.push({
            gridIndex: gi + 1,
            type: 'category',
            data: hist.counts.map((_, k) => String(hist.lo + (k + 0.5) * binWidth)),
            axisLabel: { show: false },
            axisTick: { show: false },
        });
        runs.forEach((r, k) => {
            const color = blend(panel.color, 0.28 * k);
            series.push({
                name: `${panel.label}, run ${r.run}`,
                type: 'line',
                xAxisIndex: gi,
                yAxisIndex: gi,
                showSymbol: false,
                sampling: 'lttb',
                animation: false,
                lineStyle: { width: interactive ? 1.1 : 2, color },
                itemStyle: { color },
                data: r.time_ns.map((t, j) => [t, r.values[panel.key][j]]),
            });
        });
        series.push({
            name: `${panel.label} histogram`,
            type: 'bar',
            xAxisIndex: gi + 1,
            yAxisIndex: gi + 1,
            barCategoryGap: '8%',
            animation: false,
            itemStyle: { color: panel.color },
            data: hist.counts,
            silent: true,
        });
    });
    return {
        animation: false,
        textStyle: axisText,
        grid: grids,
        xAxis,
        yAxis,
        tooltip: interactive ? { trigger: 'axis' } : { show: false },
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        series,
    };
}
